/**
 * KHO TRANG THAI CUA UNG DUNG.
 *
 * Khong dung Redux, khong dung Zustand. Mot lop nho + useSyncExternalStore la
 * du, va quan trong hon: no giu cho lich su chinh sua la thu HANG DAU cua ung
 * dung chu khong phai mot cai plugin dat canh.
 *
 * Mo hinh du lieu:
 *   byte PDF goc  (BAT BIEN, khong bao gio bi sua)
 *   + danh sach EditCommand  (chinh la "tai lieu da sua")
 *   -> chi khi luu moi vat chat hoa thanh byte moi, do loi Rust/PDFium lam.
 *
 * Vi vay dispatch KHONG goi core.apply(). Cac lenh duoc tich luy o day; toan bo
 * danh sach duoc gui xuong loi mot lan tai thoi diem luu. Nho the undo/redo
 * khong bao gio phai dong bo hai nguon su that.
 */
import type {
  Annotation,
  CommandRecord,
  DocumentId,
  DocumentInfo,
  EditCommand,
  PdfError,
  TextBlock,
} from '@pdfeditor/types';

import { coalesce, describe, invert, type InversionContext } from './commands';
import type { CoreFlavor } from '../core';
import type { EditScope } from '../core/edit/scope';
import { buildInitialPlan, replayPagePlan, type PagePlan } from '../core/pages/plan';
import { sortAnnotationsByZ, nextZAbove } from '../core/annotations/order';

export type DocStatus = 'empty' | 'loading' | 'ready' | 'error';

/** Hai the cua bang thong tin noi. */
export type InspectorTab = 'objects' | 'diagnostics';

/**
 * BO CONG CU.
 *
 * "Tô sáng" da bi go bo theo yeu cau cua nguoi dung - no khong con o day, khong
 * con tren thanh cong cu, va khong con duong nao trong ung dung sinh ra mot dau
 * 'highlight'. Bo mot cong cu thi phai bo het duong di cua no; de lai mot nua se
 * cho ra mot phim tat con song trong khi khong con nut nao bam duoc.
 */
export type ToolId =
  | 'select'
  | 'text'
  | 'note'
  | 'freetext'
  | 'square'
  | 'circle'
  | 'line'
  | 'arrow'
  | 'image'
  | 'ink';

/** Cac cong cu ve mot hinh khoi bang thao tac keo. Dung chung mot duong xu ly. */
export const SHAPE_TOOLS = ['square', 'circle', 'line', 'arrow'] as const;
export type ShapeTool = (typeof SHAPE_TOOLS)[number];

export function isShapeTool(tool: ToolId): tool is ShapeTool {
  return (SHAPE_TOOLS as readonly string[]).includes(tool);
}

/**
 * Sap xep chu thich theo THU TU CHONG LOP.
 *
 * XUAT LAI chu khong dinh nghia lai: dinh nghia nam canh duong ghi, trong
 * core/annotations/order.ts, va replayAnnotations() dung DUNG ham nay. Do la
 * dieu lam cho bang doi tuong va tep xuat ra noi cung mot dieu.
 *
 * Mot ban sao o day se troi khoi ban kia - va da tung: kho trang thai sap theo
 * `z` de ve bang, con duong ghi giu thu tu them vao, nen mot lan "đưa lên trên
 * một bậc" doi duoc bang ma khong doi duoc tep.
 */
export { sortAnnotationsByZ, nextZAbove };

export interface HistoryEntry {
  record: CommandRecord;
  /** null = khong dao nguoc duoc; undo se dung lai o day va noi ro ly do. */
  inverse: EditCommand | null;
  label: string;
}

export interface AppState {
  status: DocStatus;
  error: PdfError | null;
  fileName: string | null;
  fileSizeBytes: number;
  doc: DocumentId | null;
  info: DocumentInfo | null;

  coreFlavor: CoreFlavor | null;
  coreLabel: string;

  zoom: number;
  currentPage: number;
  tool: ToolId;
  sidebarOpen: boolean;

  /**
   * BANG THONG TIN (doi tuong + chan doan) dang mo hay khong.
   *
   * Truoc day hai bang nay nam trong mot cot co dinh ben phai, chiem 320px man
   * hinh vinh vien cho thong tin chi thinh thoang moi can. Nguoi dung noi thang
   * la khong can no thuong truc. Bay gio no la mot lop noi, va trang thai dong /
   * mo nam O DAY chu khong trong thanh phan hien thi: doi cong cu, chon doi
   * tuong khac, hay ve lai cay React deu KHONG duoc dong bang ma nguoi dung da
   * co y mo ra.
   */
  inspectorOpen: boolean;
  /**
   * The dang xem trong bang thong tin.
   *
   * Tu doi theo thu nguoi dung vua chon (xem selectBlock / selectAnnotation):
   * chon mot khoi chu thi the "Chẩn đoán" moi la the tra loi duoc cau hoi ho
   * dang hoi, con chon mot doi tuong thi the "Đối tượng" moi dung.
   */
  inspectorTab: InspectorTab;

  /** Khoi van ban dang chon - khoi CHA, nhu viec gom nhom tra ve. */
  selectedBlock: TextBlock | null;
  /**
   * PHAM VI dang chon ben trong khoi do: mot run ("Ô"), mot dong ("Dòng"), hay
   * ca khoi.
   *
   * Ton tai vi viec gom nhom keo ca mot bang vao mot khoi tren tai lieu that.
   * Neu giao dien chi biet den khoi thi moi lan sua deu la sua ca bang. Day la
   * thu quyet dinh nguoi dung SUA cai gi, nen no la trang thai hang dau chu
   * khong phai mot bien phu tro cua thanh phan hien thi. Xem core/edit/scope.ts.
   */
  selectedScope: EditScope | null;
  /** Cac khoi van ban da trich cho trang dang xem (dung de bam chon). */
  blocksByPage: ReadonlyMap<number, TextBlock[]>;

  /**
   * CHIEU CUA CAC LENH text.replace DANG CO HIEU LUC: blockId -> van ban hien hanh.
   *
   * Day KHONG phai mot lich su thu hai. No duoc suy ra hoan toan tu history
   * bang applyToProjection(), y het cach `annotations` duoc suy ra - undo goi
   * lenh nghich va ban do nay tu quay ve. Tang hien thi doc no de ve lop chinh
   * sua de len trang, va do la cach duy nhat de Ctrl+Z nhin thay duoc.
   */
  editedText: ReadonlyMap<string, string>;

  /**
   * SO DO TRANG DANG CO HIEU LUC.
   *
   * Suy ra HOAN TOAN tu tien to danh sach lenh, y het `editedText`. Khong bao
   * gio duoc sua truc tiep: moi thay doi di qua dispatch/undo/redo roi
   * recomputePagePlan() dung lai no tu dau.
   *
   * QUAN TRONG - MOT PHEP TINH DUY NHAT: cho nay va duong luu trong
   * PdfCoreBrowser.save() goi CUNG ham replayPagePlan() tren CUNG danh sach
   * lenh. Nho vay thanh ben khong the hien mot thu tu trang khac voi thu tu
   * trong tep xuat ra. Duong sua chu da tung tra gia vi hai phep tinh hinh hoc
   * song song troi khoi nhau - xem core/edit/coverRect.ts.
   */
  pagePlan: PagePlan;
  /** So trang cua TEP GOC. Diem xuat phat cua moi lan phat lai so do. */
  originalPageCount: number;

  /**
   * Chu thich dang co hieu luc, LUON sap theo `z` tang dan.
   *
   * Thu tu cua mang nay khong phai chi tiet noi bo: no la thu tu ghi vao
   * /Annots. Xem sortAnnotationsByZ().
   */
  annotations: Annotation[];
  /**
   * Doi tuong dang chon tren trang / trong bang doi tuong.
   *
   * MOT gia tri duy nhat cho ca hai cho: bam mot dong trong bang phai lam sang
   * dung doi tuong do tren trang va nguoc lai. Hai bien lua chon song song se
   * lech nhau ngay lan bam thu hai.
   */
  selectedAnnotationId: string | null;
  history: HistoryEntry[];
  /** So lenh dang co hieu luc. history.slice(0, historyIndex) la ban hien hanh. */
  historyIndex: number;

  /** Thong bao thoang qua o goc man hinh. */
  toast: { text: string; tone: 'info' | 'warn' | 'error' } | null;
}

const INITIAL: AppState = {
  status: 'empty',
  error: null,
  fileName: null,
  fileSizeBytes: 0,
  doc: null,
  info: null,
  coreFlavor: null,
  coreLabel: 'Đang khởi tạo lõi…',
  zoom: 1.15,
  currentPage: 0,
  tool: 'select',
  sidebarOpen: true,
  inspectorOpen: false,
  inspectorTab: 'objects',
  selectedBlock: null,
  selectedScope: null,
  blocksByPage: new Map(),
  editedText: new Map(),
  pagePlan: [],
  originalPageCount: 0,
  annotations: [],
  selectedAnnotationId: null,
  history: [],
  historyIndex: 0,
  toast: null,
};

export const ZOOM_STEPS = [0.5, 0.67, 0.8, 1, 1.15, 1.35, 1.6, 2, 2.5, 3, 4];

export class AppStore {
  private state: AppState = INITIAL;
  private readonly listeners = new Set<() => void>();
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  // --- Ket noi voi React ---------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): AppState => this.state;

  private set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  // --- Vong doi tai lieu ---------------------------------------------------

  setCore(flavor: CoreFlavor, label: string): void {
    this.set({ coreFlavor: flavor, coreLabel: label });
  }

  beginLoad(fileName: string, sizeBytes: number): void {
    this.set({
      status: 'loading',
      error: null,
      fileName,
      fileSizeBytes: sizeBytes,
      selectedBlock: null,
      selectedScope: null,
      blocksByPage: new Map(),
      editedText: new Map(),
      pagePlan: [],
      originalPageCount: 0,
    });
  }

  loaded(doc: DocumentId, info: DocumentInfo): void {
    this.set({
      status: 'ready',
      doc,
      info,
      error: null,
      currentPage: 0,
      annotations: [],
      selectedAnnotationId: null,
      history: [],
      historyIndex: 0,
      selectedBlock: null,
      selectedScope: null,
      blocksByPage: new Map(),
      editedText: new Map(),
      pagePlan: buildInitialPlan(info.pageCount),
      originalPageCount: info.pageCount,
    });
    if (info.signed) {
      this.notify(
        'Tài liệu có chữ ký số. Mọi chỉnh sửa sẽ làm chữ ký mất hiệu lực.',
        'warn',
      );
    } else if (info.hasXfa) {
      this.notify('Biểu mẫu XFA: tài liệu này chỉ đọc, không chỉnh sửa được.', 'warn');
    } else if (info.tagged) {
      this.notify(
        'PDF có gắn thẻ cấu trúc. Chỉnh sửa thô sẽ phá vỡ khả năng truy cập (PDF/UA).',
        'info',
      );
    }
  }

  failed(error: PdfError): void {
    this.set({ status: 'error', error, doc: null, info: null });
  }

  reset(): void {
    this.set({ ...INITIAL, coreFlavor: this.state.coreFlavor, coreLabel: this.state.coreLabel });
  }

  // --- Dieu khien khung xem ------------------------------------------------

  setZoom(zoom: number): void {
    this.set({ zoom: clamp(zoom, 0.1, 8) });
  }

  zoomIn(): void {
    const next = ZOOM_STEPS.find((z) => z > this.state.zoom + 0.001);
    this.setZoom(next ?? this.state.zoom);
  }

  zoomOut(): void {
    const lower = [...ZOOM_STEPS].reverse().find((z) => z < this.state.zoom - 0.001);
    this.setZoom(lower ?? this.state.zoom);
  }

  setCurrentPage(index: number): void {
    if (index === this.state.currentPage) return;
    this.set({ currentPage: index });
  }

  setTool(tool: ToolId): void {
    this.set({ tool });
  }

  toggleSidebar(): void {
    this.set({ sidebarOpen: !this.state.sidebarOpen });
  }

  // --- Bang thong tin noi --------------------------------------------------

  /**
   * Mo / dong bang thong tin.
   *
   * KHONG dat lai `inspectorTab` o day: mo lai phai tra ve dung the vua xem,
   * neu khong thi moi lan dong ra mo vao nguoi dung lai phai tim lai cho cu.
   */
  toggleInspector(): void {
    this.set({ inspectorOpen: !this.state.inspectorOpen });
  }

  setInspectorOpen(open: boolean): void {
    if (open === this.state.inspectorOpen) return;
    this.set({ inspectorOpen: open });
  }

  setInspectorTab(tab: InspectorTab): void {
    this.set({ inspectorTab: tab });
  }

  // --- Van ban / lua chon --------------------------------------------------

  setBlocksForPage(pageIndex: number, blocks: TextBlock[]): void {
    const next = new Map(this.state.blocksByPage);
    next.set(pageIndex, blocks);
    this.set({ blocksByPage: next });
  }

  /**
   * Chon mot khoi va - quan trong hon - PHAM VI ben trong no.
   *
   * Hai gia tri luon di cung nhau. De chung lech nhau (pham vi cua khoi nay,
   * khoi cua khoi kia) la mo duong cho viec sua nham don vi, nen o day chi co
   * MOT loi vao dat ca hai.
   */
  selectBlock(block: TextBlock | null, scope: EditScope | null = null): void {
    this.set({
      selectedBlock: block,
      selectedScope: block ? scope : null,
      // Chon chu thi bo chon doi tuong - xem ghi chu o selectAnnotation().
      ...(block ? { selectedAnnotationId: null, inspectorTab: 'diagnostics' as const } : {}),
    });
  }

  /**
   * Chon mot doi tuong chu thich (hoac bo chon voi null).
   *
   * Chon mot doi tuong thi BO chon khoi van ban: hai thanh thuoc tinh khac nhau
   * dua vao hai lua chon nay, va de ca hai cung sang se hien dong thoi hai bang
   * thuoc tinh noi ve hai thu khac nhau.
   */
  selectAnnotation(id: string | null): void {
    if (id === null) {
      this.set({ selectedAnnotationId: null });
      return;
    }
    this.set({
      selectedAnnotationId: id,
      selectedBlock: null,
      selectedScope: null,
      inspectorTab: 'objects',
    });
  }

  /**
   * Doi cho mot doi tuong len tren / xuong duoi MOT bac trong thu tu chong lop.
   *
   * Tra ve lenh can phat, hoac null khi doi tuong da o dau/cuoi. Ham thuan tuy -
   * ben goi la nguoi dispatch, de mot lan bam ra dung mot buoc hoan tac.
   *
   * `z` moi la DIEM GIUA cua hai lang gieng ma no vuot qua. Nho vay chi can mot
   * lenh annotation.update duy nhat, va lenh nghich cua no (invert() lay gia tri
   * cu cua dung truong `z`) tra ve chinh xac vi tri cu.
   */
  reorderCommand(id: string, direction: 'up' | 'down'): EditCommand | null {
    const list = this.state.annotations;
    const index = list.findIndex((a) => a.id === id);
    if (index < 0) return null;

    // Chi doi cho TRONG CUNG MOT TRANG: bang doi tuong chi liet ke doi tuong cua
    // trang dang xem, nen "len mot bac" phai co nghia trong dung danh sach do.
    const page = list[index]?.pageIndex;
    const samePage = list.filter((a) => a.pageIndex === page);
    const local = samePage.findIndex((a) => a.id === id);
    const neighbourIndex = direction === 'up' ? local + 1 : local - 1;
    const neighbour = samePage[neighbourIndex];
    if (!neighbour) return null;

    const beyond = samePage[direction === 'up' ? local + 2 : local - 2];
    const nz = neighbour.z ?? 0;
    const z =
      beyond !== undefined
        ? (nz + (beyond.z ?? 0)) / 2
        : direction === 'up'
          ? nz + 1
          : nz - 1;

    return { kind: 'annotation.update', id, patch: { z } };
  }

  /** Doi rieng pham vi, giu nguyen khoi. Dung cho nut nới rộng / thu hẹp. */
  setSelectedScope(scope: EditScope | null): void {
    if (!this.state.selectedBlock) return;
    this.set({ selectedScope: scope });
  }

  // --- Lich su chinh sua ---------------------------------------------------

  private inversionContext(): InversionContext {
    const map = new Map<string, Annotation>();
    for (const a of this.state.annotations) map.set(a.id, a);
    return { annotationsById: map, pagePlan: this.state.pagePlan };
  }

  /**
   * Dung lai so do trang tu TIEN TO danh sach lenh.
   *
   * Day la duong hoan tac THAT cua thao tac trang, va no manh hon viec ap lenh
   * nghich: khong co lenh nghich nao bi ap, nen khong co lenh nghich nao co the
   * SAI. Bo mot lenh 'page.remove' khoi tien to thi moi lenh xoay/sap xep truoc
   * do van con nguyen hieu luc, tu dong va dung theo dinh nghia.
   *
   * Goi sau MOI lan tien to doi: dispatch, undo, redo.
   */
  private recomputePagePlan(): void {
    if (this.state.originalPageCount === 0) return;
    const replayed = replayPagePlan(
      buildInitialPlan(this.state.originalPageCount),
      this.activeCommands(),
    );
    if (!replayed.ok) {
      // Khong the xay ra qua duong giao dien: core.apply() da kiem tra tung
      // lenh truoc khi no vao lich su. Neu van xay ra thi PHAI noi ra chu khong
      // duoc giu mot so do cu khong con khop voi danh sach lenh.
      this.notify(`Không dựng lại được thứ tự trang: ${replayed.error}`, 'error');
      return;
    }
    this.set({ pagePlan: replayed.plan });
  }

  /**
   * XOA MOT CHU THICH - loi vao DUY NHAT cho viec xoa.
   *
   * Loc "doi tuong da khong con" khong phai phong thu thua, no sua mot loi that:
   * mot phim Delete di qua HAI bo nghe phim doc lap (bo nghe toan cuc trong
   * App.tsx va bo nghe rieng cua viewer/AnnotationLayer.tsx), ca hai deu goi
   * duong xoa nay. Mot lan bam sinh ra HAI lenh annotation.remove cung ma.
   *
   * Lenh thu hai la mot qua bom hen gio. Tren man hinh khong ai thay gi - doi
   * tuong da bien mat sau lenh dau. Nhung LUC XUAT TEP, duong ghi phat lai tung
   * lenh len mot ban lam viec sach va tu choi lenh thu hai ("chú thích này
   * không còn nữa"), roi CA LAN XUAT that bai. Trieu chung ma nguoi dung thay:
   * xoa mot doi tuong xong thi khong bao gio luu duoc tep nua, va thong bao loi
   * khong he nhac den viec xoa. No cung lam Ctrl+Z phai bam hai lan cho mot
   * thao tac.
   *
   * Chan o DAY chu khong o mot trong hai bo nghe: day la cho moi duong xoa gap
   * nhau, nen mot nguon phat lenh thu ba sau nay cung duoc bao ve san.
   */
  removeAnnotation(id: string): void {
    if (!this.state.annotations.some((a) => a.id === id)) return;
    this.dispatch({ kind: 'annotation.remove', id });
  }

  /**
   * Ghi nhan mot lenh. Day la LOI VAO DUY NHAT de thay doi tai lieu.
   */
  dispatch(command: EditCommand, origin = 'local'): void {
    const now = Date.now();
    const ctx = this.inversionContext();
    const inverse = invert(command, ctx);

    // Bo nhanh redo: da re huong lich su thi cac lenh phia sau khong con nghia.
    const live = this.state.history.slice(0, this.state.historyIndex);
    const last = live[live.length - 1];

    if (last && last.record.origin === origin && inverse !== null && last.inverse !== null) {
      const merged = coalesce(last.record.command, command, now - last.record.timestampMs);
      if (merged) {
        // Giu lenh nghich CU cua muc lich su: no tro ve trang thai truoc CA
        // chuoi thao tac, chu khong phai truoc rieng lenh vua roi. Day chinh
        // la diem mau chot cua viec gop lenh.
        const entry: HistoryEntry = {
          record: { command: merged, origin, timestampMs: now },
          inverse: last.inverse,
          label: describe(merged),
        };
        const history = [...live.slice(0, -1), entry];
        this.applyToProjection(command);
        this.set({ history, historyIndex: history.length });
        this.recomputePagePlan();
        return;
      }
    }

    const entry: HistoryEntry = {
      record: { command, origin, timestampMs: now },
      inverse,
      label: describe(command),
    };
    const history = [...live, entry];
    this.applyToProjection(command);
    this.set({ history, historyIndex: history.length });
    this.recomputePagePlan();

    if (inverse === null) {
      this.notify(`“${entry.label}” không hoàn tác được.`, 'warn');
    }
  }

  canUndo(): boolean {
    const entry = this.state.history[this.state.historyIndex - 1];
    return entry !== undefined && entry.inverse !== null;
  }

  canRedo(): boolean {
    return this.state.historyIndex < this.state.history.length;
  }

  undo(): void {
    const entry = this.state.history[this.state.historyIndex - 1];
    if (!entry) return;
    if (!entry.inverse) {
      this.notify(`Không thể hoàn tác “${entry.label}” — thao tác này là một chiều.`, 'warn');
      return;
    }
    this.applyToProjection(entry.inverse);
    this.set({ historyIndex: this.state.historyIndex - 1 });
    this.recomputePagePlan();
  }

  redo(): void {
    const entry = this.state.history[this.state.historyIndex];
    if (!entry) return;
    this.applyToProjection(entry.record.command);
    this.set({ historyIndex: this.state.historyIndex + 1 });
    this.recomputePagePlan();
  }

  /** Danh sach lenh dang co hieu luc - chinh la thu se gui xuong loi khi luu. */
  activeCommands(): EditCommand[] {
    return this.state.history
      .slice(0, this.state.historyIndex)
      .map((e) => e.record.command);
  }

  /**
   * Chieu lenh len trang thai hien thi phia client.
   *
   * CHU Y: day chi la ban xem truoc. Su that cuoi cung nam o loi Rust khi luu.
   * Cac lenh cap trang / anh / che thong tin khong the xem truoc bang pdf.js nen
   * chi duoc ghi vao lich su.
   */
  private applyToProjection(command: EditCommand): void {
    switch (command.kind) {
      case 'text.replace': {
        // Lenh nghich cua text.replace cung la mot text.replace (xem
        // commands.ts), nen undo di qua DUNG nhanh nay va tu dat lai van ban
        // cu. Khong can nhanh xu ly rieng cho undo.
        const next = new Map(this.state.editedText);
        next.set(command.blockId, command.newText);
        this.set({ editedText: next });
        break;
      }
      // Ca ba nhanh chu thich deu di qua sortAnnotationsByZ(). Sap xep tai DUY
      // NHAT cho nay - neu de tang hien thi tu sap thi bang doi tuong se dung
      // mot thu tu con duong ghi dung mot thu tu khac.
      case 'annotation.add': {
        this.set({
          annotations: sortAnnotationsByZ([...this.state.annotations, command.annotation]),
        });
        break;
      }
      case 'annotation.remove': {
        const remaining = this.state.annotations.filter((a) => a.id !== command.id);
        this.set({
          annotations: remaining,
          // Bo lua chon khi doi tuong dang chon bien mat. Giu lai se de thanh
          // thuoc tinh tro toi mot doi tuong khong con ton tai, va nut Xóa o do
          // se khong lam gi ca ma cung khong bao gi.
          ...(this.state.selectedAnnotationId === command.id
            ? { selectedAnnotationId: null }
            : {}),
        });
        break;
      }
      case 'annotation.update': {
        this.set({
          annotations: sortAnnotationsByZ(
            this.state.annotations.map((a) =>
              a.id === command.id ? { ...a, ...command.patch } : a,
            ),
          ),
        });
        break;
      }
      case 'page.insert':
      case 'page.remove':
      case 'page.reorder':
      case 'page.rotate':
        // CO Y BO TRONG. So do trang khong duoc ap tung lenh o day: no duoc
        // DUNG LAI tu tien to danh sach lenh trong recomputePagePlan(), goi
        // ngay sau moi lan tien to doi. Ap them o day la ap hai lan.
        break;

      default:
        break;
    }
  }

  // --- Thong bao -----------------------------------------------------------

  notify(text: string, tone: 'info' | 'warn' | 'error' = 'info'): void {
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.set({ toast: { text, tone } });
    this.toastTimer = setTimeout(() => {
      this.toastTimer = null;
      this.set({ toast: null });
    }, tone === 'info' ? 4000 : 7000);
  }

  dismissToast(): void {
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.toastTimer = null;
    this.set({ toast: null });
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Mot the hien duy nhat cho ca ung dung. */
export const store = new AppStore();
